/**
 * DEC-001: Shopify-style Experience View for Stock Entry DocType
 * 
 * This view creates a Shopify-like stock entry detail page with 3 tabs:
 * 1. Details - Explicit sections with key Stock Entry information
 * 2. Tab 2 - Dynamically populated from first SystemView tab (after Details)
 * 3. Tab 3 - Dynamically populated from second SystemView tab
 * 
 * Details Tab Sections:
 * - stock-entry-overview: Stock Entry identification and key information
 * - stock-entry-details: Core stock entry details (type, dates, warehouses, company)
 * - items: Stock Entry Detail child table (items being moved)
 * - status-summary: Stock Entry status and document state
 */

import type { DocTypeView } from '../types';

export const stockEntryShopifyView: DocTypeView = {
  id: 'stock-entry-shopify-v1',
  doctype: 'Stock Entry',
  style: 'shopify',
  context: undefined,
  status: 'active',
  label: 'Stock Entry – Shopify Style',
  description: 'Shopify-style layout for Stock Entry detail page with 3 tabs (Details + 2 dynamic tabs from meta)',
  formView: {
    sections: [
      // ============================================
      // DETAILS TAB - Explicit Sections
      // ============================================

      // 1. Stock Entry Overview (header info)
      {
        id: 'stock-entry-overview',
        title: 'Stock Entry Overview',
        description: 'Stock Entry identification and key status information',
        layout: [
          {
            columns: [
              {
                fieldname: 'name',
                mode: 'read',
                visibility: 'default',
                width: '2/3',
              },
              {
                fieldname: 'docstatus',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'posting_date',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'posting_time',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 2. Stock Entry Details (core information)
      {
        id: 'stock-entry-details',
        title: 'Stock Entry Details',
        description: 'Core stock entry information',
        layout: [
          {
            columns: [
              {
                fieldname: 'stock_entry_type',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'from_warehouse',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'to_warehouse',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'company',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'remarks',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 3. Items (Stock Entry Detail child table)
      {
        id: 'items',
        title: 'Items',
        description: 'Items being moved in this stock entry',
        layout: [
          {
            columns: [
              {
                fieldname: 'items', // Child table (Stock Entry Detail)
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 4. Status Summary (Value Summary card — outgoing/incoming/difference)
      {
        id: 'status-summary',
        title: 'Status Summary',
        description: 'Stock Entry value summary (outgoing, incoming, difference)',
        layout: [
          {
            columns: [
              {
                fieldname: 'total_outgoing_value',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'total_incoming_value',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'value_difference',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
        ],
      },

      // 5. Timeline (creation → posted → submitted/cancelled/modified)
      // The widget reads audit fields (creation, modified, owner, modified_by, docstatus)
      // directly from formData. We only need a real meta field here so the
      // section survives formViewToTabs's "must have ≥1 real field" filter.
      {
        id: 'stock-entry-timeline',
        title: 'Timeline',
        description: 'Creation, posting, and modification history',
        layout: [
          {
            columns: [
              {
                fieldname: 'posting_date',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 6. Actions (lifecycle-aware: Save/Submit/Cancel/Amend)
      // The widget reads docstatus from formData directly. We reference a real
      // meta field here (stock_entry_type) to survive the section filter.
      {
        id: 'stock-entry-actions',
        title: 'Actions',
        description: 'Lifecycle actions for this Stock Entry',
        layout: [
          {
            columns: [
              {
                fieldname: 'stock_entry_type',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // ============================================
      // TAB 2 - Dynamically populated from first SystemView tab (after Details)
      // ============================================
      // Note: Tab name will be resolved dynamically from meta
      // Common Stock Entry tabs: "Stock Entry Details", "More Information", etc.
      // The extraction function will find the first tab after "__details"
      {
        id: 'stock-entry-additional-details',
        title: 'Additional Details',
        description: 'Additional stock entry fields from ERPNext meta',
        // Special marker: layout will be populated dynamically from SystemView tab
        layout: [],
        // This will pull fields from the first tab after "Details" in SystemView
        // The tab name will be resolved at runtime from meta
        _dynamicFromSystemTab: '__first_tab_after_details__',
      },

      // ============================================
      // TAB 3 - Dynamically populated from second SystemView tab
      // ============================================
      {
        id: 'stock-entry-more-info',
        title: 'More Information',
        description: 'Additional stock entry information from ERPNext meta',
        layout: [],
        // This will pull fields from the second tab after "Details" in SystemView
        _dynamicFromSystemTab: '__second_tab_after_details__',
      },
    ],
    customFieldBucket: {
      enabled: true,
      sectionTitle: 'Additional ERP Fields',
      visibilityDefault: 'collapsed',
    },
  },
  createdBy: 'system',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

